import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Downloads a PDF file with platform-specific handling
 * - Web: Uses browser download
 * - Android: Saves to device storage
 */
export const downloadPdf = async (
  pdfUrl: string, 
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Android/iOS: Save to device storage
      await downloadPdfNative(pdfUrl, fileName, onSuccess, onError);
    } else {
      // Web: Use browser download
      await downloadPdfWeb(pdfUrl, fileName, onSuccess, onError);
    }
  } catch (error) {
    console.error('PDF download error:', error);
    onError?.(error instanceof Error ? error.message : 'Download failed');
  }
};

/**
 * Shares a PDF file with platform-specific handling
 * - Web: Opens in new tab
 * - Android: Opens native share dialog
 */
export const sharePdf = async (
  pdfUrl: string, 
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    if (Capacitor.isNativePlatform()) {
      // Android/iOS: Save to device and share
      await sharePdfNative(pdfUrl, fileName, onSuccess, onError);
    } else {
      // Web: Open in new tab
      await sharePdfWeb(pdfUrl, fileName, onSuccess, onError);
    }
  } catch (error) {
    console.error('PDF share error:', error);
    onError?.(error instanceof Error ? error.message : 'Share failed');
  }
};

/**
 * Downloads PDF on native platforms (Android/iOS) - saves to device only
 */
const downloadPdfNative = async (
  pdfUrl: string,
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    let pdfData: string;
    
    // Handle different URL types
    if (pdfUrl.startsWith('data:')) {
      // Data URI - extract base64 data
      pdfData = pdfUrl.split(',')[1];
    } else if (pdfUrl.startsWith('blob:')) {
      // Blob URL - convert to base64
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      pdfData = await blobToBase64(blob);
    } else {
      // Regular URL - fetch and convert
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      pdfData = await blobToBase64(blob);
    }

    // Ensure fileName has .pdf extension
    const finalFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

    // Save to device storage
    await Filesystem.writeFile({
      path: finalFileName,
      data: pdfData,
      directory: Directory.Documents,
      recursive: true
    });

    onSuccess?.();
  } catch (error) {
    console.error('Native PDF download error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to save PDF to device');
  }
};

/**
 * Shares PDF on native platforms (Android/iOS) - saves and opens share dialog
 */
const sharePdfNative = async (
  pdfUrl: string,
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    let pdfData: string;
    
    // Handle different URL types
    if (pdfUrl.startsWith('data:')) {
      // Data URI - extract base64 data
      pdfData = pdfUrl.split(',')[1];
    } else if (pdfUrl.startsWith('blob:')) {
      // Blob URL - convert to base64
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      pdfData = await blobToBase64(blob);
    } else {
      // Regular URL - fetch and convert
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      pdfData = await blobToBase64(blob);
    }

    // Ensure fileName has .pdf extension
    const finalFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

    // Save to device storage
    const savedFile = await Filesystem.writeFile({
      path: finalFileName,
      data: pdfData,
      directory: Directory.Documents,
      recursive: true
    });

    // Share the file
    await Share.share({
      title: 'PDF Document',
      text: `PDF: ${fileName}`,
      url: savedFile.uri,
      dialogTitle: 'Share PDF'
    });

    onSuccess?.();
  } catch (error) {
    console.error('Native PDF share error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to share PDF');
  }
};

/**
 * Downloads PDF on web platforms
 */
const downloadPdfWeb = async (
  pdfUrl: string,
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    let downloadUrl: string;
    let shouldRevoke = false;

    if (pdfUrl.startsWith('data:')) {
      // Data URI - create blob URL for download
      const byteCharacters = atob(pdfUrl.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      downloadUrl = URL.createObjectURL(blob);
      shouldRevoke = true;
    } else {
      downloadUrl = pdfUrl;
    }

    // Create download link
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL if created
    if (shouldRevoke) {
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    }

    onSuccess?.();
  } catch (error) {
    console.error('Web PDF download error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to download PDF');
  }
};

/**
 * Shares PDF on web platforms - opens in new tab
 */
const sharePdfWeb = async (
  pdfUrl: string,
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
) => {
  try {
    // For web, open PDF in new tab
    window.open(pdfUrl, '_blank');
    onSuccess?.();
  } catch (error) {
    console.error('Web PDF share error:', error);
    onError?.(error instanceof Error ? error.message : 'Failed to open PDF');
  }
};

/**
 * Converts a Blob to base64 string
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data:application/pdf;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
